import { CheckCircle2, Loader2, Circle, XCircle } from 'lucide-react';
import type { AgentPlan, AgentPlanStep } from './canvas-types';

interface PlanStepRowProps {
  step: AgentPlanStep;
  index: number;
}

function PlanStepRow({ step, index }: PlanStepRowProps) {
  const isCompleted = step.status === 'completed';
  const isRunning = step.status === 'running';
  const isFailed = step.status === 'failed';
  const isPending = step.status === 'pending';

  return (
    <div
      data-testid={`plan-step-row-${step.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 0',
        opacity: isPending ? 0.5 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      <span
        style={{ flexShrink: 0, display: 'flex', alignItems: 'center', width: 14 }}
        data-testid={`plan-step-icon-${step.id}`}
      >
        {isCompleted && (
          <CheckCircle2 size={13} style={{ color: '#22C55E' }} />
        )}
        {isRunning && (
          <Loader2 size={13} className="animate-spin" style={{ color: '#6366F1' }} />
        )}
        {isFailed && (
          <XCircle size={13} style={{ color: '#EF4444' }} />
        )}
        {isPending && (
          <Circle size={13} style={{ color: '#D1D5DB' }} strokeWidth={1.5} />
        )}
      </span>

      <span
        style={{
          fontSize: 12.5,
          color: isCompleted ? '#16A34A' : isRunning ? '#1A1A1A' : isFailed ? '#DC2626' : '#5F6D82',
          fontWeight: isRunning ? 600 : 400,
          flex: 1,
          lineHeight: 1.4,
        }}
      >
        {index + 1}. {step.label}
      </span>

      {isRunning && (
        <span
          style={{
            fontSize: 10,
            color: '#6366F1',
            background: '#EEF2FF',
            borderRadius: 4,
            padding: '1px 5px',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          Running
        </span>
      )}
      {isCompleted && (
        <span
          style={{
            fontSize: 10,
            color: '#15803D',
            background: '#DCFCE7',
            borderRadius: 4,
            padding: '1px 5px',
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          Done
        </span>
      )}
    </div>
  );
}

interface AgentPlanCardProps {
  plan: AgentPlan;
}

export function AgentPlanCard({ plan }: AgentPlanCardProps) {
  const completedCount = plan.steps.filter((s) => s.status === 'completed').length;
  const totalCount = plan.steps.length;
  const allDone = completedCount === totalCount && totalCount > 0;
  const hasRunning = plan.steps.some((s) => s.status === 'running');

  return (
    <div
      data-testid={`agent-plan-card-${plan.id}`}
      style={{
        borderRadius: 10,
        border: '1px solid #DCE3ED',
        background: '#F7F9FC',
        padding: '12px 14px',
        marginBottom: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1A1A1A', flex: 1 }}>
          {plan.title || 'Agent Plan'}
        </span>
        {hasRunning && !allDone && (
          <span style={{ fontSize: 11, color: '#6366F1', fontWeight: 600 }}>
            Step {completedCount + 1} of {totalCount}
          </span>
        )}
        {allDone && (
          <span
            data-testid={`agent-plan-done-badge-${plan.id}`}
            style={{ fontSize: 11, color: '#15803D', fontWeight: 600 }}
          >
            Complete
          </span>
        )}
        {!hasRunning && !allDone && totalCount > 0 && (
          <span style={{ fontSize: 11, color: '#5F6D82' }}>
            {completedCount}/{totalCount}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {plan.steps.map((step, i) => (
          <PlanStepRow key={step.id} step={step} index={i} />
        ))}
      </div>

      {totalCount > 0 && (
        <div
          style={{
            marginTop: 10,
            height: 3,
            borderRadius: 2,
            background: '#DCE3ED',
            overflow: 'hidden',
          }}
        >
          <div
            data-testid={`agent-plan-progress-${plan.id}`}
            style={{
              height: '100%',
              width: `${Math.round((completedCount / totalCount) * 100)}%`,
              background: allDone ? '#22C55E' : '#6366F1',
              transition: 'width 0.4s ease',
              borderRadius: 2,
            }}
          />
        </div>
      )}
    </div>
  );
}
